import { Button, Form, Modal, TableProps, Tag } from 'antd'
import dayjs from 'dayjs'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { apiCreatePricing, apiDeletePricing, apiFilterPricings } from '../../api'
import defaultParams from '../../assets/constants/default_params'
import PricingForm from '../../components/outlet/PricingForm'
import CustomTable from '../../components/table'
import { useLoading } from '../../context/LoadingContext'
import { IPricingAgreement } from '../../models/outlet.modal'
import { authSelector } from '../../redux/slices/authSlice'
import PermUtils from '../../utils/perm-utils'
import { toastError, toastSuccess } from '../../utils/toast'

const fmtDate = (v?: string | null) => {
    if (!v) return '—'
    const d = dayjs(v)
    if (!d.isValid() || d.year() < 2000) return '—'
    return d.format('DD/MM/YYYY')
}

const fmtCurrency = (v?: number | null) => {
    if (v == null) return '—'
    return v.toLocaleString('vi-VN')
}

type Props = {
    outletId: string
    cpCode: string
}

export default function TabPricing({ outletId, cpCode }: Props) {
    const [data, setData] = useState<IPricingAgreement[]>([])
    const [total, setTotal] = useState<number>(0)
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [isOpenCreate, setIsOpenCreate] = useState<boolean>(false)
    const [form] = Form.useForm()
    const { setIsLoading } = useLoading()
    const { account } = useSelector(authSelector)
    const canAdd = PermUtils.hasAnyAuthority(account?.listPermission, [PermUtils.canAddOutletPricing])
    const canDelete = PermUtils.hasAnyAuthority(account?.listPermission, [PermUtils.canDeleteOutletPricing])

    const fetchData = useCallback(async () => {
        if (!outletId) return
        try {
            setIsLoading(true)
            const resp: any = await apiFilterPricings({
                outletId,
                limit: pageSize,
                offset: pageSize * (currentPage - 1),
            })
            if (resp?.data == null) {
                setData([])
                setTotal(0)
                return
            }
            setData(resp.data.data ?? [])
            setTotal(Number(resp.data.total ?? 0))
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [outletId, currentPage, pageSize, setIsLoading])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const handleCreate = async (values: any) => {
        try {
            setIsLoading(true)
            const resp: any = await apiCreatePricing(values)
            if (resp.status === 200) {
                toastSuccess('Thêm thỏa thuận giá thành công!')
                setIsOpenCreate(false)
                form.resetFields()
                fetchData()
                return
            }
            toastError('Có lỗi xảy ra!')
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const handleDelete = async (id: string) => {
        try {
            setIsLoading(true)
            const resp = await apiDeletePricing(id)
            if (resp.status === 200) {
                toastSuccess('Xoá thành công!')
                fetchData()
                return
            }
            toastError('Có lỗi xảy ra!')
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const columns: TableProps<IPricingAgreement>['columns'] = [
        {
            key: 'STT',
            title: 'STT',
            render: (_, __, index) => ++index + (pageSize * (currentPage - 1)),
            width: 60,
        },
        {
            key: 'package',
            title: 'Gói cước',
            render: (_, r) => `${r.packageCode || `#${r.partnerPackageId}`} (${r.packageCpCode || '—'})`,
        },
        {
            key: 'agreedPrice',
            title: 'Giá thỏa thuận',
            align: 'right',
            render: (_, r) => {
                if (r.agreedPrice != null) return fmtCurrency(r.agreedPrice)
                if (r.defaultPrice != null) {
                    return <span style={{ color: '#999' }} title='Giá mặc định của gói cước'>{fmtCurrency(r.defaultPrice)}</span>
                }
                return <span style={{ color: '#999' }}>Mặc định</span>
            },
        },
        {
            key: 'agreedCommission',
            title: 'Hoa hồng thỏa thuận',
            align: 'right',
            render: (_, r) => {
                if (r.agreedCommission != null) return fmtCurrency(r.agreedCommission)
                if (r.defaultCommission != null) {
                    return <span style={{ color: '#999' }} title='Hoa hồng mặc định của gói cước'>{fmtCurrency(r.defaultCommission)}</span>
                }
                return <span style={{ color: '#999' }}>Mặc định</span>
            },
        },
        {
            key: 'effectiveFrom',
            title: 'Từ ngày',
            dataIndex: 'effectiveFrom',
            render: v => fmtDate(v),
        },
        {
            key: 'effectiveTo',
            title: 'Đến ngày',
            dataIndex: 'effectiveTo',
            render: v => v ? fmtDate(v) : <span style={{ color: '#999' }}>—</span>,
        },
        {
            key: 'isActive',
            title: 'Trạng thái',
            render: (_, r) => r.isActive ? <Tag color='green'>Đang hiệu lực</Tag> : <Tag>Đã đóng</Tag>,
        },
        {
            key: 'agreedNote',
            title: 'Ghi chú',
            dataIndex: 'agreedNote',
        },
        {
            key: 'action',
            title: 'Tác vụ',
            align: 'center',
            width: 100,
            render: (_, r) => (
                canDelete ? <Button danger size='small' onClick={() => handleDelete(r.id)}>Xoá</Button> : null
            ),
        },
    ]

    return (
        <div>
            {canAdd && (
                <div style={{ textAlign: 'end', marginBottom: 16 }}>
                    <Button
                        type='primary'
                        disabled={!cpCode}
                        title={cpCode ? '' : 'Đại lý chưa có công ty (cpCode). Vui lòng cập nhật trước.'}
                        onClick={() => setIsOpenCreate(true)}
                    >
                        + Thêm thỏa thuận
                    </Button>
                </div>
            )}
            <CustomTable
                namePage='thỏa thuận'
                rowKey='id'
                columns={columns}
                dataSource={data}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={total}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
            <Modal
                title='Thêm thỏa thuận giá'
                open={isOpenCreate}
                footer={null}
                onCancel={() => setIsOpenCreate(false)}
                destroyOnClose
            >
                <PricingForm
                    form={form}
                    outletId={outletId}
                    cpCode={cpCode}
                    onSubmit={handleCreate}
                    onCancel={() => setIsOpenCreate(false)}
                />
            </Modal>
        </div>
    )
}
